/**
 * @file DataRequest.jsx
 * @description Data request page with Material Design 3 styling
 */

import React from "react";
import { useViewportHeight } from "../utils/viewportUtils";
import { Icon } from "./Icon";

export const DataRequest = () => {
  const viewportHeight = useViewportHeight();
  return (
    <div style={{
      height: `calc(${viewportHeight} - 64px)`,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{ 
        textAlign: 'center', 
        padding: '24px 16px 0 16px',
        flexShrink: 0
      }}>
        <h1 style={{ 
          fontSize: 'var(--md-sys-typescale-headline-large-size)',
          fontWeight: 'var(--md-sys-typescale-headline-large-weight)',
          color: 'var(--md-sys-color-on-surface)',
          margin: '0'
        }}>
          Data Request
        </h1>
      </div>

      {/* Scrollable Content */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        minHeight: 0,
        padding: '24px 16px'
      }}>
        <div className="container" style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Content */}
          <div style={{
            backgroundColor: 'var(--md-sys-color-surface-container-low)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <p style={{ marginBottom: '16px', lineHeight: '1.6' }}>
              memorize.life stores the data you provide when you create an
              account and use the site. See our{" "}
              <a 
                href="/privacy-policy"
                style={{ 
                  color: 'var(--md-sys-color-primary)',
                  textDecoration: 'none'
                }}
              >
                privacy policy
              </a>{" "}
              for more information. This data includes your email address, password,
              and reading challenge progress. We do not share any personal
              data with anyone besides what is visible in the group reading
              features, and we do not use it for any purpose other than to
              provide you with the services on this site. If you would like to
              change your displayed name, you can do so on the{" "}
              <a 
                href="/settings"
                style={{ 
                  color: 'var(--md-sys-color-primary)',
                  textDecoration: 'none'
                }}
              >
                settings page
              </a>.
            </p>

            <h2 style={{
              fontSize: 'var(--md-sys-typescale-headline-medium-size)',
              fontWeight: 'var(--md-sys-typescale-headline-medium-weight)',
              color: 'var(--md-sys-color-on-surface)',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              Request Your Data
            </h2>
            <p style={{ marginBottom: '24px', lineHeight: '1.6' }}>
              If you would like to request a copy of the data we have stored
              for you, please use the button below. We will send you a copy of
              your data within 30 days.
            </p>

            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <p style={{ 
                marginBottom: '16px', 
                color: 'var(--md-sys-color-on-surface-variant)',
                fontSize: '14px'
              }}>
                Click to open a pre-filled email template with all necessary information
              </p>
              <md-filled-button href="mailto:mail@memorize.life?subject=[Data Request] - Request for Personal Data&body=Dear memorize.life Team,%0D%0A%0D%0AI am writing to request a copy of all personal data you have stored about me in accordance with data protection regulations.%0D%0A%0D%0AAccount Information:%0D%0A- Email address: [Please enter your email address]%0D%0A- Display name: [Please enter your display name if different from email]%0D%0A- Date of account creation (if known): [Please enter if known]%0D%0A%0D%0ARequest Details:%0D%0A- Date of request: [Please enter today's date]%0D%0A- I understand you will provide this data within 30 days%0D%0A- Please include all stored data including: email, display name, reading challenge progress, notification preferences, and any other personal information%0D%0A%0D%0APlease send the data export to this email address.%0D%0A%0D%0AThank you for your assistance.%0D%0A%0D%0ABest regards,%0D%0A[Please enter your name]">
                <Icon name="download" slot="icon" />
                Request Data
              </md-filled-button>
            </div>

            <h2 style={{
              fontSize: 'var(--md-sys-typescale-headline-medium-size)',
              fontWeight: 'var(--md-sys-typescale-headline-medium-weight)',
              color: 'var(--md-sys-color-on-surface)',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              Delete Your Data
            </h2>
            <p style={{ marginBottom: '24px', lineHeight: '1.6' }}>
              If you would like to delete your account and all associated
              data, please use the button below. We will delete your account
              and all associated data within 30 days.
            </p>

            <div style={{ textAlign: 'center', marginBottom: '0' }}>
              <p style={{ 
                marginBottom: '16px', 
                color: 'var(--md-sys-color-on-surface-variant)',
                fontSize: '14px'
              }}>
                Click to open a pre-filled email template with all necessary information
              </p>
              <md-filled-tonal-button href="mailto:mail@memorize.life?subject=[Data Deletion] - Request for Account and Data Deletion&body=Dear memorize.life Team,%0D%0A%0D%0AI am writing to request the complete deletion of my account and all associated personal data in accordance with data protection regulations.%0D%0A%0D%0AAccount Information:%0D%0A- Email address: [Please enter your email address]%0D%0A- Display name: [Please enter your display name if different from email]%0D%0A- Date of account creation (if known): [Please enter if known]%0D%0A%0D%0ADeletion Request Details:%0D%0A- Date of request: [Please enter today's date]%0D%0A- I understand you will delete my account and data within 30 days%0D%0A- Please delete ALL data including: account information, email, display name, reading challenge progress, notification preferences, and any other personal information%0D%0A- I understand this action is irreversible%0D%0A%0D%0AConfirmation:%0D%0AI confirm that I want to permanently delete my memorize.life account and all associated data. I understand that:%0D%0A- This action cannot be undone%0D%0A- I will lose access to all reading challenges and progress%0D%0A- I will need to create a new account if I wish to use the service again%0D%0A%0D%0APlease confirm the deletion via email once completed.%0D%0A%0D%0AThank you for your assistance.%0D%0A%0D%0ABest regards,%0D%0A[Please enter your name]">
                <Icon name="delete" slot="icon" />
                Delete Data
              </md-filled-tonal-button>
            </div>
          </div>

          {/* Back to Home */}
          <div style={{ textAlign: 'center', marginTop: '32px', marginBottom: '32px' }}>
            <md-filled-button href="/">
              <Icon name="home" slot="icon" />
              Back to Home
            </md-filled-button>
          </div>
        </div>
      </div>
    </div>
  );
};
